<template>
  <div class="layout-regions">
    <MobileHeader
      :class="{ hide: showRegions }"
    >
      <template #action>
        <OperationTypeNavigationMobile :showHmePolicy="currentShaft?.policy?.show_heavy_mobile_equipment" />
      </template>
      <div class="menu">
        <ul class="menu__items">
          <li class="menu-item">
            <button
              :aria-label="$t('regions.search_regions_and_issues')"
              class="menu-item__link menu-item__link--icon"
              :disabled="selectedStationId && !stationRegionTree"
              type="button"
              @click="openMobileSearch"
            >
              <div class="menu-item__label">
                <UseSvg
                  class="icon"
                  name="search"
                />
              </div>
            </button>
          </li>
        </ul>
      </div>
    </MobileHeader>
    <div
      :class="{
        'layout-regions__header hide-for-medium': true,
        hide: showRegions,
      }"
    >
      <button
        class="select__trigger typo-ignore typo-title"
        type="button"
        @click="showRegions = true"
      >
        <div class="select__label-wrapper">
          <PolygonSvg
            v-if="selectedStationRegion"
            :bounds="selectedStationRegion.bounds"
            :class="`region-item__thumbnail region-item__thumbnail--no-margin region-item__thumbnail--${selectedStationRegion.region_type}`"
            :project="false"
            :size="20"
          />
          <span class="select__label">
            {{
              selectedStationRegion
                ? selectedStationRegion.name
                : $t('regions.all_regions')
            }}
          </span>
        </div>
        <div class="select__arrow">
          <UseSvg
            class="icon icon--small"
            name="arrow_down_16"
          />
        </div>
      </button>
      <SwitchButton
        v-model="stationView"
        :items="stationViewOptions"
        @update:model-value="refreshView"
      />
    </div>

    <div
      :class="{
        'layout-regions__regions': true,
        'show-for-medium': !showRegions,
      }"
    >
      <OperationTypeNavigation :showHmePolicy="currentShaft?.policy?.show_heavy_mobile_equipment" />
      <div class="layout-regions__regions-header hide-for-medium">
        <button
          type="button"
          @click="showRegions = false"
        >
          <UseSvg
            class="icon"
            name="close"
          />
        </button>
        <span class="layout-regions__regions-title">
          {{ $t('regions.select_region') }}
        </span>
      </div>

      <div class="layout-regions__search-bar show-for-medium">
        <UseSvg
          :class="{ icon: true, 'icon--darker': !stationRegionSearch }"
          name="search"
        />
        <input
          v-model="stationRegionSearch"
          class="layout-regions__search-input typo-small-title"
          :disabled="!selectedStationId || !stationRegionTree"
          :placeholder="$t('regions.search_all_regions')"
        >
        <button
          v-if="stationRegionSearch"
          class="button button--tiny button--transparent"
          type="button"
          @click="stationRegionSearch = ''"
        >
          <UseSvg
            class="icon icon--small"
            name="close_circle_16"
          />
        </button>
      </div>

      <FormRow
        v-if="shaft"
        :label="$t('shafts.group.stations.select_station')"
        :modifiers="['narrow']"
      >
        <div class="form-row__input">
          <SkeletonElement
            v-if="shaft && loadingStations"
            :height="40"
            :radius="6"
          />
          <Select
            v-if="!loadingStations && stationList"
            ref="stationSelect"
            v-model="selectedStationId"
            :items="stationList"
            :modifiers="['input', 'base', 'short-dropdown']"
            :placeholder="$t('shafts.group.stations.select_station')"
            @click.passive="checkScroll"
          />
        </div>
      </FormRow>

      <RegionTree
        v-if="selectedStationId && stationRegionTree && !loadingStationsRegions"
        :class="{ 'hide-for-medium': stationRegionSearch }"
        :modifiers="['manage-scroll']"
        :project="false"
        :regionTree="stationRegionTree"
        :selectedRegion="selectedStationRegion"
        @select="selectRegion"
      />

      <Loader
        v-if="selectedStationId && !stationRegionTree"
        class="show-for-medium"
        :modifiers="['height-fix']"
      />

      <EmptyMessage
        v-if="stationRegionSearch && stationRegionResults.length === 0"
        :message="$t('general.no_results')"
        :modifiers="['grow']"
      >
        <UseSvg name="magnifying_glass_circle" />
      </EmptyMessage>
      <template v-if="stationRegionSearch && stationRegionResults.length > 0">
        <div class="layout-regions__results-header typo-small">
          {{ $t('general.results') }}
        </div>
        <div class="layout-regions__regions-tree scroll-container">
          <ul>
            <RegionItem
              v-for="region in stationRegionResults"
              :key="region.id"
              class="layout-regions__regions-tree"
              :ignoreSelected="true"
              :project="false"
              :region="region"
              :showChildren="false"
              @select="selectRegionFromSearch"
            />
          </ul>
        </div>
      </template>
    </div>

    <div
      :class="{
        'layout-regions__content': true,
        'show-for-medium': showRegions,
      }"
    >
      <!-- right start -->
      <div class="layout-regions__issues">
        <div class="layout-regions__search-bar show-for-medium">
          <UseSvg
            :class="{ icon: true, 'icon--darker': !stationIssueSearch }"
            name="search"
          />
          <input
            v-model="stationIssueSearch"
            class="layout-regions__search-input typo-small-title"
            :disabled="!stationIssueList || stationIssueList.length === 0"
            :placeholder="$t('regions.search_all_issues')"
          >
          <button
            v-if="stationIssueSearch"
            class="button button--tiny button--transparent"
            type="button"
            @click="stationIssueSearch = ''"
          >
            <UseSvg
              class="icon icon--small"
              name="close_circle_16"
            />
          </button>
        </div>

        <NoticeMessage
          v-if="issuesStore.loadingNotification && issuesStore.loading"
          iconStyle="info"
          :message="$t('general.long_load_message')"
          style="margin: 5px"
        />
        <IssuesSkeletonLoader
          v-if="selectedStationId && issuesStore.loading"
          class="show-for-medium"
        />
        <div
          v-if="!issuesStore.loading"
          :class="{
            'layout-regions__issues-content': true,
            hide: !selectedStationId || stationIssueSearch,
          }"
        >
          <div class="layout-regions__current-region show-for-medium">
            {{
              selectedStationRegion
                ? selectedStationRegion.name
                : $t('regions.all_regions')
            }}
          </div>
          <div class="layout-regions__issues-header">
            <span
              v-if="stationIssueList"
              class="layout-regions__issues-count typo-small-nav-item"
            >
              {{ stationIssueList.length }}
              {{ $t('activerecord.models.plants/issue', { count: stationIssueList.length }) }}
            </span>

            <div
              v-if="stationIssueList?.length > 0"
              class="layout-regions__csv-download typo-small"
            >
              <a :href="csvIssueDownloadPath || 'javascript:void(0);'">
                <div class="layout-regions__csv-download__link">
                  <span>{{ $t('general.csv') }}</span>
                  <PushDown />
                </div>
              </a>
            </div>

            <IssueFilter
              v-if="isActiveFilter || stationIssueList?.length > 0"
              :sortOptions="sortByOptions"
              :typeFilter="false"
              @set-filter-options="setFilterOptions"
            />
          </div>

          <RecyclerView
            v-if="stationIssueList"
            ref="issueScrollView"
            :class="{
              'layout-regions__list': true,
              'show-for-medium': stationView !== 'list',
            }"
            :count="stationIssueList.length"
            :gap="0.125"
            :height="7"
            hideScrollingX
            name="layout-regions__list-item-"
          >
            <template #default="{ index, style, key }">
              <IssueCard
                :key="key"
                :isLocatable="true"
                isSmallCard
                :issue="stationIssueList[index]"
                overrideIssuePath="shaftV2RegionsIssuesTypeStation"
                :responsive="false"
                :selected="stationIssueList[index] === selectedSelectedIssue"
                :showDescription="false"
                :style="style"
                @locate="selectIssueOnList(stationIssueList[index])"
              />
            </template>
          </RecyclerView>
        </div>

        <Loader
          v-if="selectedStationId && !stationIssueList"
          class="show-for-medium"
        />
        <div
          v-if="selectedStationId && stationIssueSearch"
          class="layout-regions__issues-content"
        >
          <EmptyMessage
            v-if="stationIssueSearch && stationIssueResults.length === 0"
            :message="$t('general.no_results')"
          >
            <UseSvg name="magnifying_glass_circle" />
          </EmptyMessage>
          <template v-if="stationIssueSearch && stationIssueResults.length > 0">
            <div class="layout-regions__results-header typo-small">
              {{ $t('general.results') }}
            </div>
            <div class="layout-regions__list scroll-container">
              <ul>
                <IssueCard
                  v-for="issue in stationIssueResults"
                  :key="issue.id"
                  isSmallCard
                  :issue="issue"
                  noLink
                  overrideIssuePath="shaftV2RegionsIssuesTypeStation"
                  :responsive="false"
                  :showDescription="false"
                  @select="selectIssueFromSearch(issue)"
                />
              </ul>
            </div>
          </template>
        </div>
      </div>
      <!-- right end -->
      <!-- middle start -->
      <div
        :class="{
          'layout-regions__map layout-regions__map--center': true,
          'show-for-medium': stationView !== 'map',
        }"
      >
        <Loader
          v-if="issuesStore.loading"
          class="layout-regions__mobile-issues-loading hide-for-medium"
        />
        <LeafletMap
          v-if="loadMap && !loadingStation && selectedStation && !selectedStation?.has_pending_tile_process"
          :key="issuesStore.componentKey"
          ref="map"
          :diagram="selectedStation.diagram_identifier"
          @init="onMapInit"
          @marker-click="selectIssueOnMap"
          @polygon-click="selectRegionOnMap"
        />
        <EmptyMessage
          v-else-if="!map && selectedStation?.has_pending_tile_process"
          :message="$t('shafts.group.stations.diagram_loading_description')"
          :title="$t('shafts.group.stations.diagram_loading_title')"
        />
        <EmptyMessage
          v-else
          :title="$t('shafts.group.stations.no_station_selection.title')"
        >
          <StationSelectSvg />
        </EmptyMessage>
        <Loader
          v-if="loadingStation"
          :modifiers="['overlay']"
        />
      </div>
      <!-- middle end -->
    </div>

    <div
      v-if="mobileSearchOpen"
      class="layout-regions__mobile-search hide-for-medium"
    >
      <div class="layout-regions__search-bar">
        <UseSvg
          class="icon"
          name="search"
        />
        <input
          ref="mobileSearchInput"
          v-model="mobileSearch"
          class="layout-regions__search-input typo-small-title"
          :placeholder="$t('regions.search_regions_and_issues')"
        >
        <UseSvg
          class="icon"
          name="close_circle_24"
          @click="mobileSearchOpen = false"
        />
      </div>
      <EmptyMessage
        v-if="mobileSearch && stationRegionResults.length === 0 && stationIssueResults.length === 0"
        :message="$t('general.no_results')"
      >
        <UseSvg name="magnifying_glass_circle" />
      </EmptyMessage>
      <ul class="layout-regions__search-results scroll-container">
        <RegionItem
          v-for="region in stationRegionResults"
          :key="region.id"
          :ignoreSelected="true"
          :project="false"
          :region="region"
          :showChildren="false"
          @select="selectRegionFromSearch"
        />
        <IssueCard
          v-for="issue in stationIssueResults"
          :key="issue.id"
          isSmallCard
          :issue="issue"
          noLink
          overrideIssuePath="shaftV2RegionsIssuesTypeStation"
          :responsive="false"
          :showDescription="false"
          @select="selectIssueFromSearch(issue)"
        />
      </ul>
    </div>

    <Loader
      v-if="selectedStationId && (!map || !stationRegionTree || !stationIssueList)"
      class="hide-for-medium"
      :modifiers="['overlay']"
    />
  </div>
  <router-view v-if="['shaftV2RegionsIssuesTypeStation', 'shaftV2IssuesLocationTypeStation'].includes($route.name)" />
</template>

<script setup>
import { ref, watch, computed, onMounted, onUnmounted, nextTick, reactive } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useShaftV2 } from '@/functions/use_remote_object';
import { sortingFunctions, filterBy } from '@/functions/utils.js';
import I18n from 'i18n-js';
import useAjax from '@/functions/use_ajax';
import debounce from '@/vendor/debounce';
import Searcher from '@/functions/searcher';
import ancestry from '@/functions/ancestry';
import regionPolygonOptions from '@/functions/region_polygon_options';
import { compareByAreaAndIdInverse } from '@/functions/compare_regions';
import { geoJSONPolygonToLatLngs, latLngToGeoJSON, rgeoPolygonToGeoJSON, rgeoToLatLng } from '@/functions/geospatial_conversions';
import getRiskRatingLevel from '@/functions/simm/get_risk_rating_level';
import { useActiveFilter } from '@/functions/composables/active_filter.js';
import booleanContains from '@turf/boolean-contains';
import { filterBySelectedRegion } from '@/functions/filter_by_selected_region.js';

// Stores
import { useIssuesStore } from '@/stores/issues/issuesStore';

// Components
import MobileHeader from '@/components/generic/mobile_header.vue';
import RegionItem from '@/components/regions/region_item.vue';
import RegionTree from '@/components/regions/region_tree.vue';
import Select from '@/components/form/select.vue';
import FormRow from '@/components/form/row.vue';
import SwitchButton from '@/components/form/switch_button.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import IssueCard from '@/components/issues/issue_card.vue';
import EmptyMessage from '@/components/generic/empty_message.vue';
import SkeletonElement from '@/components/generic/skeleton_element.vue';
import LeafletMap from '@/vendor/components/leaflet_map.vue';
import Loader from '@/components/generic/loader.vue';
import RecyclerView from '@/components/generic/recycler_view.vue';
import IssueFilter from '@/components/issues/issue_filter.vue';
import OperationTypeNavigation from '@/components/shaftsV2/operations/operation_type_navigation.vue';
import OperationTypeNavigationMobile from '@/components/shaftsV2/operations/operation_type_navigation_mobile.vue';
import IssuesSkeletonLoader from '@/components/issues/issues_skeleton_loader.vue';
import NoticeMessage from '@/components/generic/notice_message.vue';

// Icons
import { DotIcon } from '@/vendor/leaflet_mods';
import StationSelectSvg from '@/images/station-select.svg';
import PushDown from '@/images/push-down.svg';
import PolygonSvg from '@/components/generic/polygon_svg.vue';

// Set up store
const issuesStore = useIssuesStore();

const IssueIcon = DotIcon.extend({
  options: {
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  },
});

const RISK_ICONS = {
  // @ts-ignore
  low: new IssueIcon({ className: 'issue-point issue-point--low-risk' }),
  // @ts-ignore
  medium: new IssueIcon({ className: 'issue-point issue-point--medium-risk' }),
  // @ts-ignore
  significant: new IssueIcon({ className: 'issue-point issue-point--significant-risk' }),
  // @ts-ignore
  high: new IssueIcon({ className: 'issue-point issue-point--high-risk' }),
};

const SELECTED_RISK_ICONS = Object.entries(RISK_ICONS).reduce((obj, [risk, icon]) => {
  // @ts-ignore
  obj[risk] = new IssueIcon({ className: icon.options.className + ' issue-point--selected' });
  return obj;
}, {});

const props = defineProps({
  issueType: { type: String, required: false, default: null },
  organisationId: { type: String, required: true },
  stationId: { type: String, required: false, default: null },
  shaftId: { type: String, required: true },
  inspectionId: { type: String, required: false, default: null },
  issueId: { type: String, required: false, default: null },
});

const ajax = useAjax();
const sortFunctions = sortingFunctions();
const router = useRouter();
const route = useRoute();
const currentShaft = useShaftV2({ organisationId: route.params.organisationId, shaftId: route.params.shaftId });

const issueSearch = ref('');
const shaft = ref(null);
const view = ref('main');

const issueList = ref([]);
const issueResults = ref([]);

const mobileSearchOpen = ref(false);
const mobileSearch = ref('');
const mobileSearchInput = ref(null);

const sortBy = ref('');
const loadMap = ref(true);
const { isActiveFilter, validateFilterOptions } = useActiveFilter();

// Station declarations
const showRegions = ref(false);
const map = ref(null);
const issueScrollView = ref(null);
const selectedStationId = ref(null);
const selectedStation = ref(null);
const stationList = ref(null);
const loadingStation = ref(false);
const loadingStations = ref(false);
const loadingStationsRegions = ref(true);

const stationRegions = ref(null);
const stationIssues = ref(null);
const stationView = ref('map'); // use the one on the other page and update this code
const stationRegionMap = ref(null);
const stationRegionTree = ref([]);
const stationIssueMap = ref(null);
const stationSelect = ref();
const selectedStationRegion = ref(null);
const selectedSelectedIssue = ref(null);

const stationRegionSearch = ref('');
const stationIssueSearch = ref('');
const stationRegionResults = ref([]);
const stationIssueResults = ref([]);
const conditions = ref([]);
const priorities = ref([]);
const routerNameList = ['shaftV2IssuesTypeStations', 'shaftV2IssuesTypeStation'];

let stationRegionsSearcher = null;
let stationIssuesSearcher = null;

const sortByOptions = [
  { label: I18n.t('issues.highest_risk'), value: 'highest_risk'},
  { label: I18n.t('issues.lowest_risk'), value: 'lowest_risk'},
  { label: I18n.t('general.oldest'), value: 'oldest'},
  { label: I18n.t('general.newest'), value: 'newest'},
  { label: I18n.t('issues.highest_repair_priority'), value: 'highest_repair_priority'},
  { label: I18n.t('issues.lowest_repair_priority'), value: 'lowest_repair_priority'},
];

const baseUrl = `/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}`;
const ENDPOINTS = computed(() => {
  return {
    shaft: baseUrl,
    setElements: `${baseUrl}/sets`,
    stations: `${baseUrl}/stations`,
  };
});

onMounted(() => {
  loadStationData();
});

onUnmounted(() => {
  ajax.abort(); // Abort any ajax calls when leaving this component
});

watch(route, () => {
  !selectedStationId.value && loadStationData();
});

/**
 * Reloads the map and update issues when filtering occurs
 * or a different diagram is selected
 */
watch(
  () => [selectedStationId.value],
  async () => {
    if (!isIncluded(routerNameList, route.name)) return;
    loadIssues(selectedStationId.value);
  }
);

watch(issuesStore, () => {
  renderIssues();
});

watch(() => props.issueType, async() => {
  deselectIssue();
  mobileSearchOpen.value = false;
  mobileSearch.value = '';
  issueResults.value = [];
  issueList.value = [];

  selectedStationId.value = null;
  selectedStation.value = null;
  stationRegionResults.value = [];
  stationIssueResults.value = [];
  stationRegionTree.value = [];
  loadingStationsRegions.value = true;

  selectedStationId.value = route.params.stationId;
  return !stationList.value && await loadStations();
});

watch(
  mobileSearch,
  debounce(
    () => {
      if (!mobileSearchOpen.value || !mobileSearch.value) return;
      stationRegionResults.value = stationRegionsSearcher.search(mobileSearch.value);
      stationIssueResults.value = stationIssuesSearcher.search(mobileSearch.value);
    }, 300, true
  )
);

/**
 * Reloads the map and update issues when filtering occurs
 * or a different diagram is selected
 */
watch(
  () => [selectedStationId.value, sortBy.value, conditions.value, priorities.value],
  async () => {
    loadMap.value = false;
    await nextTick();
    loadMap.value = true;
  }
);

async function loadStationData() {
  if (!isIncluded(routerNameList, route.name)) return;

  selectedStationId.value = route.params?.stationId;
  !stationList.value && await loadStations();
  await loadShaft();
  return await loadIssues(selectedStationId.value);
}

async function loadShaft() {
  if (props.shaftId) {
    let { ok, data } = await ajax(ENDPOINTS.value.shaft);

    if (ok) {
      shaft.value = data;
    }
  }
}

async function renderIssues() {
  stationIssues.value = issuesStore.issues;
  stationIssueMap.value = new Map();
  if (!map.value) return; // in case user navigates away

  const isActiveFilter = sortBy.value || conditions.value?.length > 0 || priorities.value?.length > 0;

  if (isActiveFilter) {
    const filteredIssues = filteredIssueList(issuesStore.issues);
    filteredIssues.forEach(loadIssue);
  } else {
    stationIssues.value.forEach(loadIssue);
  }

  stationIssuesSearcher = new Searcher(stationIssueMap.value, { keys: ['title', 'id'] });

  refreshView();

  await loadSelectedContent();
}

async function selectIssueOnList(issue) {
  stationView.value = 'map';
  await refreshView();
  await selectIssue(issue);
}

async function selectIssueFromSearch(issue) {
  mobileSearchOpen.value = false;
  issueSearch.value = '';
  view.value = 'list';
  selectIssueOnList(issue);
}

async function selectRegionFromSearch(region) {
  mobileSearchOpen.value = false;
  stationRegionSearch.value = '';
  await selectRegion(region);
}

async function openMobileSearch() {
  mobileSearchOpen.value = true;
  mobileSearch.value = '';
  issueResults.value = [];

  stationRegionResults.value = [];
  stationIssueResults.value = [];

  await nextTick();
  mobileSearchInput.value.focus();
}

const csvIssueDownloadPath = computed(() => {
  let endpoint = `/internal/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}/graph/download?type=multiple`;

  endpoint += conditions.value.map((item) => `&conditions[]=${item}`).join('');
  endpoint += priorities.value.map((item) => `&priorities[]=${item}`).join('');

  endpoint += `&issue_type=station&station_id=${selectedStationId.value}`;
  if (selectedStationRegion.value) {
    endpoint += `&station_region_id=${selectedStationRegion.value['id']}`;
  }

  if (stationIssueMap.value?.size > 0) {
    const issues = Array.from(stationIssueMap.value.values());

    const earliestIssue = issues.reduce((start, current) => {
      return start['inspection']['date'] < current['inspection']['date'] ? start : current;
    });

    if (earliestIssue) {
      endpoint += `&start_date=${earliestIssue['inspection']['date']}`;
    }
  }

  return endpoint;
});

function setFilterOptions(options) {
  sortBy.value = options.sortOption;
  conditions.value = options.conditions;
  priorities.value = options.priorities;

  /* Check if filter has any active filter options */
  validateFilterOptions(options);
}

/* MOST STATION CODE */
watch(selectedStationId, async(value) => {
  if (route.params.issueType !== 'stations') return;
  value && router.push({
    name: 'shaftV2IssuesTypeStation',
    params: { stationId: value },
    query: route.query
  });
  deselectIssue();
  await loadStation(value);
});

watch(stationRegionSearch, debounce(() => {
  if (!stationRegionSearch.value) return;

  stationRegionResults.value = stationRegionsSearcher.search(stationRegionSearch.value);
}, 300, true));

watch(stationIssueSearch, debounce(() => {
  if (!stationIssueSearch.value) return;

  stationIssueResults.value = stationIssuesSearcher.search(stationIssueSearch.value);
}, 300, true));

const stationIssueList = computed(() =>
  selectedStationRegion.value
    ? filterBySelectedRegion(filteredIssueList(), selectedStationRegion.value)
    : filteredIssueList()
);

function filteredIssueList(passedIssues) {
  const issues = passedIssues || stationIssueMap.value;

  if (!issues) return null;

  let list = [...issues.values()];

  if (conditions.value.length > 0) {
    list = list.filter(i => {
      return conditions.value.includes(i.overall_condition_rating);
    });
  }

  if (priorities.value.length > 0) {
    list = list.filter(i => {
      return priorities.value.includes(i.priority);
    });
  }

  if (sortBy.value === 'highest_repair_priority' || sortBy.value === 'lowest_repair_priority') {
    const highList = filterBy(list, 'priority', 'X');
    const lowList = filterBy(list, 'priority', 'M');
    const numberList = filterBy(list, 'priority')?.sort(sortFunctions[sortBy.value]);
    let finalList = [];

    if (sortBy.value === 'highest_repair_priority') {
      finalList = [...highList, ...numberList, ...lowList];
    } else if (sortBy.value === 'lowest_repair_priority') {
      finalList = [...lowList, ...numberList, ...highList];
    }
    return finalList;
  } else {
    return list.sort(sortFunctions[sortBy.value]);
  }
}

const stationViewOptions = [
  { icon: 'crosshair_dot', value: 'map' },
  { icon: 'list_bullet_end', value: 'list' },
];

async function loadStation(stationId) {
  if (stationId) {
    loadingStation.value = true;
    let { ok, data } = await ajax(`${baseUrl}/stations/${stationId}`);

    if (ok) {
      selectedStation.value = data;
      // TODO: find a proper way to handle the delay
      setTimeout(() => {
        loadingStation.value = false;
      }, 0);
      return data;
    }
  }
}

async function loadStations() {
  if (route.params.shaftId) {
    loadingStations.value = true;
    let { ok, data } = await ajax(ENDPOINTS.value.stations);

    if (ok) {
      stationList.value = data.map((item) => ({
        label: item.name,
        value: item.id,
        diagram: item.diagram_identifier,
        name: item.name,
        start_set: item.start_set,
        end_set: item.end_set,
        id: item.id
      }));
      loadingStations.value = false;
    }
  }
}

async function loadRegions(stationId) {
  if (!props.shaftId || !stationId) return;
  loadingStationsRegions.value = true;
  let { ok, data } = await ajax(`${ENDPOINTS.value.stations}/${stationId}/station_regions`);

  if (ok) {
    stationRegions.value = data.regions;
    loadingStationsRegions.value = false;
    stationRegionMap.value = new Map();
    if (!map.value) return; // in case user navigates away

    stationRegions.value.forEach(loadRegion);
    stationRegionTree.value = ancestry(stationRegionMap.value.values());
  }
}

async function loadIssues(stationId) {
  if (!props.shaftId || !stationId) return;

  issuesStore.$reset();
  issuesStore.loading = true;

  setTimeout(() => {
    issuesStore.loadingNotification = true;
  }, 5000);

  stationIssues.value = [];
  stationIssueMap.value = new Map();

  let { ok, data } = await ajax.paginated(
    `${ENDPOINTS.value.stations}/${stationId}/issues`,
    { query: { category: 'approved_and_open' }, items: stationIssues.value }
  );

  if (ok) {
    issuesStore.issues = data;
  }

  issuesStore.loading = false;
  issuesStore.loadingNotification = false;
}

async function onMapInit() {
  await loadRegions(selectedStationId.value);
  await renderIssues();
  await loadSelectedContent();
}

async function loadSelectedContent() {
  if (!map.value) return; // in case user navigates away

  if (route.query.regionId) {
    await selectRegion(stationRegionMap.value.get(route.query.regionId), { redirect: false, goTo: !route.query.issueId });
  }
  if (route.query.issueId) {
    await selectIssue(stationIssueMap.value.get(route.query.issueId), { redirect: false });
  }
}

function checkScroll() {
  if (!selectedStationId.value) {
    stationSelect.value.dropdownContainerRef.scrollTop = 0;
  }
}

function loadRegion(region) {
  let geoJSON = rgeoPolygonToGeoJSON(region.bounds);
  let latLngs = geoJSONPolygonToLatLngs(geoJSON);
  let polygon = map.value.addPolygon(latLngs, regionPolygonOptions(region.region_type));
  let regionObject = {
    ...region,
    geoJSON,
    latLngs,
    polygon,
  };
  polygon.disableEdit();
  polygon.region = regionObject;

  stationRegionMap.value.set(region.id, reactive(regionObject));
  stationRegionsSearcher = new Searcher(stationRegionMap.value);
}

function loadIssue(issue) {
  let latLng = rgeoToLatLng(issue.location.location);
  let geoJSON = latLngToGeoJSON(latLng);
  let riskLevel = getRiskRatingLevel(issue.risk_rating);
  let zIndexOffset = issue.risk_rating * 10;
  let marker = map.value.addMarker(latLng, {
    icon: RISK_ICONS[riskLevel],
    riseOnHover: true,
    zIndexOffset,
  });
  let issueObject = {
    ...issue,
    timestamp: +new Date(issue.inspection.date),
    riskLevel,
    latLng,
    geoJSON,
    marker,
    zIndexOffset,
  };
  marker.issue = issueObject;

  stationIssueMap.value.set(issue.id, issueObject);

  if (issue.id === route.query.issueId) map.value.enableCrosshair(marker._latlng.lat, marker._latlng.lng);
}

async function refreshView() {
  await nextTick();

  switch (stationView.value) {
    case 'map':
      if (map.value) await map.value.refresh();
      break;
    case 'list':
      issueScrollView.value?.update();
      scrollToIssue(selectedSelectedIssue.value, { behavior: 'auto', block: 'top', force: true });
      break;
  }
}

// make sure clicking on a stack of objects doesn't trigger a click on all of them
let selectingObject = false;
function doSelect() {
  selectingObject = true;
  setTimeout(() => selectingObject = false, 0);
}

function deselectRegion() {
  if (selectedStationRegion.value) {
    const previousRegion = stationRegionMap.value.get(selectedStationRegion.value.id);
    if (!previousRegion) return;
    previousRegion.polygon.setStyle(regionPolygonOptions(previousRegion.region_type, false));
    previousRegion.selected = false;
  }
}

async function selectRegion(region, { goTo = true, redirect = true } = {}) {
  if (!region) return selectNoRegion({ goTo });
  if (selectingObject && goTo) return;
  selectingObject = true;

  showRegions.value = false;
  deselectRegion();

  let newRegion = stationRegionMap.value.get(region.id);
  newRegion.polygon.setStyle(regionPolygonOptions(newRegion.region_type, true));
  selectedStationRegion.value = newRegion;

  if (goTo) map.value.goToObject(newRegion.polygon);

  if (selectedSelectedIssue.value) {
    if (!booleanContains(region.geoJSON, selectedSelectedIssue.value.geoJSON)) {
      deselectIssue();
    } else {
      await nextTick();
      scrollToIssue(selectedSelectedIssue.value, { behavior: 'smooth', block: 'top', force: true });
    }
  }

  if (redirect) {
    await router.replace({ query: { regionId: region.id, issueId: selectedSelectedIssue.value?.id } });
  }

  await nextTick();
  await refreshView();
  await new Promise(resolve => {
    newRegion.selected = true;
    setTimeout(() => {
      selectingObject = false;
      resolve();
    }, 0);
  });
}

function selectRegionOnMap(polygon) {
  if (polygon.region) {
    selectRegion(polygon.region);
  }
}

async function selectNoRegion({ goTo = true } = {}) {
  if (selectingObject && goTo) return;
  doSelect();

  router.replace({ query: { regionId: null } });

  showRegions.value = false;
  deselectRegion();

  selectedStationRegion.value = null;
  await refreshView();
}

async function selectIssueOnMap(marker) {
  doSelect();

  stationView.value = 'map';
  await selectIssue(marker.issue);
}

async function selectIssue(issue, { redirect = true } = {}) {
  deselectIssue();
  let newIssue = stationIssueMap.value.get(issue?.id);
  if (!newIssue) return;
  newIssue.marker.setIcon(SELECTED_RISK_ICONS[newIssue.riskLevel]);
  newIssue.marker.setZIndexOffset(newIssue.zIndexOffset + 1000);
  selectedSelectedIssue.value = newIssue;

  if (selectedStationRegion.value && !booleanContains(selectedStationRegion.value.geoJSON, issue.geoJSON)) {
    await selectSmallestEnclosingRegion(issue);
  }

  if (redirect) {
    router.replace({ query: { regionId: selectedStationRegion.value?.id, issueId: issue.id } });
  }

  if (issue?.latLng[0] && issue?.latLng[1]) map.value.enableCrosshair(issue.latLng[0], issue.latLng[1]);

  await nextTick();
  map.value.goToObject(newIssue.marker);
  scrollToIssue(newIssue, { behavior: 'smooth', block: 'top', force: true });
}

async function selectSmallestEnclosingRegion(issue) {
  let containingRegions = [...stationRegionMap.value.values()]
    .filter(region => booleanContains(region.geoJSON, issue.geoJSON))
    .sort(compareByAreaAndIdInverse);
  if (containingRegions.length > 0) {
    await selectRegion(containingRegions[0], { goTo: false });
  } else {
    selectNoRegion({ goTo: false });
  }
}

function deselectIssue() {
  if (selectedSelectedIssue.value) {
    let previousIssue = stationIssueMap.value.get(selectedSelectedIssue.value.id);
    previousIssue.marker.setIcon(RISK_ICONS[previousIssue.riskLevel]);
    previousIssue.marker.setZIndexOffset(previousIssue.zIndexOffset);
    selectedSelectedIssue.value = null;
  }
}

function scrollToIssue(issue, options = {}) {
  const scrollToIndex = issue ? stationIssueList.value.indexOf(issue) : 0;
  const issueCardElement = issueScrollView.value.$el.querySelector('.issue-card');
  const elementHeight = issueCardElement?.style?.height?.replace('rem', '');
  const elementOffset = ((parseFloat(elementHeight) * 16) + 2) * scrollToIndex; // 1rem = 16px
  options.top = elementOffset;
  setTimeout(() => {
    issueScrollView.value?.$el?.scrollTo(options);
  }, 300);
}

function isIncluded(list, name) {
  return list.includes(name);
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';

.layout-regions--map {
  position: static;
}
.layout-regions--model {
  background-color: map-get($background-color, darkest);
  overflow: hidden;
}

.scene-container--issues {
  height: calc(100vh - rem(250));

  @include breakpoint(medium) {
    height: 100vh;
  }
}
.selected-name {
  margin: bu(1) bu(1.4);
  font-weight: 500;
}
</style>
